/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import { brand } from '../../../theme/themePrimitives';
import CustomInput from '../../reusable/input';
import CustomModal from '../../modals';
import { FilterNoneOutlined } from '@mui/icons-material';
import { getAllPipelinesThunk } from '../../../redux/thunks/pipeline';
import ImageClass from '../../../assets';
import { nameSchema } from '../../../schema';
import PipelineApi from '../../../apis/pipeline';
import { PipelineResponse } from '../../../params/pipeline';
import { replaceToken } from '../../../redux/slices';
import SelectPipeline from '../../pipeline/select_pipeline';
import SelectStage from '../../stage/select_stage';
import { settingSelectedPipeline } from '../../../redux/slices/pipeline';
import { showError } from '../../../redux/slices/error';
import { useParams } from 'react-router';
import { AppDispatch, RootState } from '../../../redux/store';
import { Box, Button, SelectChangeEvent, Typography } from '@mui/material';
import { connectExternalFormThunk, fetchFormsThunk } from '../../../redux/thunks/form';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';

const fallbackCopy = (text: string) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    try {
        document.execCommand('copy');
    } catch (err) {
        console.error('Fallback: Copy command failed', err);
    }

    document.body.removeChild(textarea);
};

/**
 * @heading1: AddPipelineModal
 * @paragraph: A modal component for adding a new pipeline. Uses Formik for form handling
 * and integrates with Redux for pipeline creation.
 * @param: {boolean} open - Whether the modal is open or not.
 * @param: {function} onClose - Function to close the modal.
 */

const ConnectExternalForm = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
    /**
     * @heading3: Variables
     * @ol:
     * initialValues: {PipelineParams} -  Initial form values for the pipeline creation form.
     * dispatch: {AppDispatch} - The Redux dispatch function to trigger actions.
     * loaders: An object containing the loader details from the Redux store.
     */
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams();
    const { forms } = useSelector((state: RootState) => state.form);
    const [, setSelectedPipelineId] = useState<number | null>(null);
    const [, setSelectedStageId] = useState<number | null>(null);
    const { pipelines, selectedPipeline } = useSelector((state: RootState) => state.pipeline);
    const { token } = useSelector((state: RootState) => state.app);
    const [showSettings, setShowSettings] = useState<string>('');
    const [copiedUrl, setCopiedUrl] = useState<boolean>(false);
    const [selectedForm, setSelectedForm] = useState<'snapps' | 'elementor' | string>('');


    // const handleCopy = (text: string) => {
    //     navigator.clipboard.writeText(text)
    //         .then(() => {
    //             // Optional: You can show a toast/snackbar here
    //             // console.log('Copied to clipboard');
    //         })
    //         .catch((err) => {
    //             console.error('Failed to copy: ', err);
    //         });
    // };

    useEffect(() => {
        const fetchAllPipelines = async () => {
            try {
                // const snappsForm = forms.find((item) => item.type === 'snapps');
                // if (snappsForm) {
                    await dispatch(getAllPipelinesThunk(id ? parseInt(id as string, 10) : 0)).unwrap();
                // }
            } catch (e) {
                // Catch errors
            }
        };
        fetchAllPipelines();
    }, [dispatch]);

    useEffect(() => {
        const snappsForm = forms.find((item) => item.type === 'snapps');
        if (snappsForm) {
            setSelectedPipelineId(snappsForm.pipeline_id);
            const fetchPipeline = async () => {
                try {
                    const response: PipelineResponse = await PipelineApi.getSinglePipelineDetail(token, snappsForm.pipeline_id || 0) as PipelineResponse;
                    if (response.success) {
                        dispatch(replaceToken(response.token));
                        dispatch(settingSelectedPipeline(response?.pipeline));
                        setSelectedPipelineId(response?.pipeline?.id || 0);
                        setSelectedStageId(snappsForm.stage_id || null);
                    } else {
                        dispatch(showError({
                            title: 'Error',
                            description: response.msg
                        }));
                        setSelectedStageId(snappsForm.stage_id || null);
                    }
                } catch (e) {
                    console.error(e);
                    setSelectedStageId(snappsForm.stage_id || null);
                }
            };
            fetchPipeline();
        } else {
            setSelectedPipelineId(null);
            setSelectedStageId(null);
        }
    }, []);

    const handlePipelineChange = async (event: SelectChangeEvent<unknown>) => {
        event.preventDefault();
        const target = event.target as HTMLInputElement;
        if (target.value === 'choose') {
            setSelectedPipelineId(null);
            setSelectedStageId(null);
        } else {
            const pipelineId = parseInt(target.value as string, 10);
            try {
                const response: PipelineResponse = await PipelineApi.getSinglePipelineDetail(token, pipelineId) as PipelineResponse;
                if (response.success) {
                    dispatch(replaceToken(response.token));
                    dispatch(settingSelectedPipeline(response?.pipeline));
                    setSelectedPipelineId(response?.pipeline?.id || 0);
                    setSelectedStageId(null);
                } else {
                    dispatch(showError({
                        title: 'Error',
                        description: response.msg
                    }));
                }
            } catch (e) {
                console.error(e);
            }
        }
    };

    const copyToClipboard = (value: string) => {
        // const text = `${window.location.origin}/forms/${showSettings}`;
        if (navigator.clipboard) {
            navigator.clipboard.writeText(value).catch(() => fallbackCopy(value));
        } else {
            fallbackCopy(value);
        }
        setCopiedUrl(true);
        setTimeout(() => setCopiedUrl(false), 2000);
    };

    const handleStageChange = (event: SelectChangeEvent<unknown>) => {
        const stageId = Number(event.target.value);
        setSelectedStageId(stageId);
    };

    const saveChangesHandler = async (values: {
        name: string;
        pipelineId: number;
        stageId: number;
    }) => {
        // const snappsForm = forms.find((item) => item.type === 'snapps');
        // const elementorForm = forms.find((item) => item.type === 'elementor');
        // const customForm = forms.find((item) => item.type === 'custom');
        try {
            const response = await dispatch(connectExternalFormThunk({
                form_name: values.name,
                type: selectedForm === 'snapps' ? 'snapps-manual' : 'elementor-manual',
                pipeline_id: values.pipelineId,
                stage_id: values.stageId,
                client_id: id ? parseInt(id as string, 10) : 0
            })).unwrap();
            if (response) {
                setShowSettings(response as string);
            }
            //  await dispatch(updateDefaultFormThunk({
            //     forms_id: [(snappsForm?.id as number), (elementorForm?.id as number), (customForm?.id as number)],
            //     pipeline_id: selectedPipelineId || 0,
            //     stage_id: selectedStageId || 0,
            //     client_id: id ? parseInt(id as string, 10) : 0
            // })).unwrap();
            // setShowSettings(false);
        } catch (e) {
            //
        }
    };

    const onCloseHandler = async () => {
        const parsedId = id ? parseInt(id as string, 10) : 0;
        try {
            await dispatch(fetchFormsThunk(parsedId)).unwrap();
        } catch (e) {
            //
        } finally {
            onClose();
        }
    };

    return <CustomModal open={open} handleClose={onCloseHandler} title={showSettings ? 'External Form Settings' : 'Connect External Form'}>
        {
            showSettings
                ? <Box>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Box display="flex" justifyContent="center" alignItems="center">
                            <Typography variant="subtitle1" color="success">A Form Created Successfully</Typography>
                        </Box>
                        <Box sx={(theme) => ({
                            border: `1px solid ${theme.palette.divider}`,
                            padding: '5px 0',
                            color: 'rgba(51,56,63,1)',
                            borderRadius: '10px'
                        })}>
                            <Box display="flex" alignItems="center" justifyContent="center">
                                <img
                                src={selectedForm === 'snapps' ? ImageClass.snappsLogo : ImageClass.elementorLogo}
                                alt={selectedForm === 'snapps' ? 'Snapps' : 'Elementor'}
                                height="140"
                                width="140" />
                            </Box>
                        </Box>
                        <CustomInput
                            fullWidth
                            value={selectedForm === 'snapps' ? `${process.env.REACT_APP_SNAPPS_FORM_LINK}${showSettings}` : `${process.env.REACT_APP_ELEMENTOR_FORM_LINK}${showSettings}`}
                            onChange={() => null}
                        />
                        {/* <Button variant="outlined" sx={{ minWidth: '140px' }} onClick={() => handleCopy(codes.snapps_code)}>Copy Webhook Link</Button> */}
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<FilterNoneOutlined />}
                            fullWidth
                            data-screenshot={selectedForm === 'snapps' ? `${process.env.REACT_APP_SNAPPS_FORM_LINK}${showSettings}` : `${process.env.REACT_APP_ELEMENTOR_FORM_LINK}${showSettings}`}
                            onClick={() => copyToClipboard(selectedForm === 'snapps' ? `${process.env.REACT_APP_SNAPPS_FORM_LINK}${showSettings}` : `${process.env.REACT_APP_ELEMENTOR_FORM_LINK}${showSettings}`)}
                        >
                            {copiedUrl ? 'Copied!' : 'Copy Webhook Link'}
                        </Button>
                    </Box>
                </Box>
                : selectedForm
                    ? <Box>
                        <Formik
                            initialValues={{
                                name: '',
                                pipelineId: 0,
                                stageId: 0
                            }}
                            validationSchema={nameSchema}
                            onSubmit={saveChangesHandler}
                        >
                            {({ values, handleChange, handleBlur, setFieldValue, errors, touched }) => (
                                <Form>
                                    <Box display="flex" flexDirection="column" gap={2}>
                                        <Typography variant="subtitle1" color="textSecondary">{selectedForm === 'snapps' ? 'Snapps' : 'Elementor'}</Typography>
                                        {/* Input field for pipeline name */}
                                        <CustomInput
                                            value={values.name}
                                            onChange={handleChange('name')}
                                            onBlur={handleBlur('name')}
                                            error={touched.name && errors.name}
                                            fullWidth
                                            label="Name"
                                            placeholder="Name"
                                            variant="outlined"
                                            mandatoryFields={true}
                                        />
                                        <Box display="flex" gap={2} alignItems="center">
                                            <Box width="100%">
                                                <SelectPipeline
                                                    label="Pipeline"
                                                    pipelines={pipelines}
                                                    onChange={(event: SelectChangeEvent<unknown>) => {
                                                        const pipelineId = parseInt(event.target.value as string, 10);
                                                        setFieldValue('pipelineId', pipelineId);
                                                        handlePipelineChange(event);
                                                    }}
                                                    value={values.pipelineId || ''}
                                                    enabledDefaultSelection={true}
                                                />
                                            </Box>
                                            <Box width="100%">
                                                <SelectStage
                                                    label="Stage"
                                                    stages={selectedPipeline?.pipeline_stages || []}
                                                    onChange={(event: SelectChangeEvent<unknown>) => {
                                                        const stageId = parseInt(event.target.value as string, 10);
                                                        setFieldValue('stageId', stageId);
                                                        handleStageChange(event);
                                                    }}
                                                    value={values.stageId || ''}
                                                />
                                            </Box>
                                        </Box>
                                        <Box display="flex" gap={2}>
                                            <Button variant="outlined" fullWidth size="large" color="secondary" onClick={() => setSelectedForm('')}>Cancel</Button>
                                            <Button variant="contained" fullWidth size="large" type="submit">Save</Button>
                                        </Box>
                                    </Box>
                                </Form>
                            )}
                        </Formik>
                    </Box>
                    : <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                        <Typography variant="h6" textAlign="center">Select External Form to continue</Typography>
                        <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
                            <Box sx={(theme) => ({
                                'border': `1px solid ${theme.palette.divider}`,
                                'padding': '5px 0',
                                'color': 'rgba(51,56,63,1)',
                                'borderRadius': '10px',
                                '&:hover': {
                                    borderColor: brand[300],
                                    cursor: 'pointer',
                                    bgcolor: brand[50]
                                }
                            })}
                                onClick={() => setSelectedForm('snapps')}
                            >
                                <Box display="flex" alignItems="center" justifyContent="center">
                                    <img src={ImageClass.snappsLogo} alt="Snapps" height="140" width="140" />
                                </Box>
                            </Box>
                            <Box sx={(theme) => ({
                                'border': `1px solid ${theme.palette.divider}`,
                                'padding': '5px 0',
                                'color': 'rgba(51,56,63,1)',
                                'borderRadius': '10px',
                                '&:hover': {
                                    borderColor: brand[300],
                                    cursor: 'pointer',
                                    bgcolor: brand[50]
                                }
                            })}
                                onClick={() => setSelectedForm('elementor')}
                            >
                                <Box display="flex" alignItems="center" justifyContent="center">
                                    <img src={ImageClass.elementorLogo} alt="Elementor" height="140" width="140" />
                                </Box>
                            </Box>
                        </Box>
                    </Box>
        }
        {/* {
            showSettings
                ? <Box display="flex" flexDirection="column">
                    <Box display="flex" gap={2} alignItems="center" my={2}>
                        <Box width="100%">
                            <SelectPipeline
                                label="Pipeline"
                                isMandatory={true}
                                pipelines={pipelines}
                                onChange={handlePipelineChange}
                                value={selectedPipelineId || ''}
                                // disabled={disabled}
                                enabledDefaultSelection={true}
                            /></Box>
                        <Box width="100%">
                            <SelectStage
                                label="Stage"
                                isMandatory={true}
                                stages={selectedPipeline?.pipeline_stages || []}
                                onChange={handleStageChange}
                                value={selectedStageId || ''}
                            // disabled={disabled}
                            />
                        </Box>
                    </Box>
                    <Box display="flex" gap={2}>
                        <Button variant="outlined" size="large" fullWidth onClick={() => setShowSettings(false)}>Cancel</Button>
                        <Button variant="contained" size="large" fullWidth onClick={saveChangesHandler}>Save Changes</Button>
                    </Box>
                </Box>
                : <Box display="flex" flexDirection="column" gap={3}>
                    <Typography variant="subtitle1" color="textSecondary" fontWeight="600">
                        Connect forms in Elementor to Gonzy via webhook or connect forms on any platform by copying a script link to your website&apos;s HTML header.
                    </Typography>
                    <Box display="flex" justifyContent="end"><Button variant="outlined" startIcon={<Settings />} onClick={() => setShowSettings(true)}>Settings</Button></Box>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Box sx={(theme) => ({
                            border: `1px solid ${theme.palette.divider}`,
                            padding: '5px 0',
                            color: 'rgba(51,56,63,1)',
                            borderRadius: '10px'
                        })}>
                            <Box display="flex" alignItems="center" justifyContent="center"><img src={ImageClass.snappsLogo} alt="Snapps" height="70" width="70" /></Box>
                        </Box>
                        <CustomInput
                            fullWidth
                            value={codes.snapps_code}
                            onChange={() => null}
                        />
                        <Button variant="outlined" sx={{ minWidth: '140px' }} onClick={() => handleCopy(codes.snapps_code)}>Copy Webhook Link</Button>
                    </Box>

                    <Box display="flex" alignItems="center" gap={1}>
                        <Box sx={(theme) => ({
                            border: `1px solid ${theme.palette.divider}`,
                            padding: '5px 0',
                            color: 'rgba(51,56,63,1)',
                            borderRadius: '10px'
                        })}>
                            <Box display="flex" alignItems="center" justifyContent="center"><img src={ImageClass.elementorLogo} alt="Elementor" height="70" width="70" /></Box>
                        </Box>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Box display="flex" alignItems="center" gap={1}><CustomInput
                                fullWidth
                                value={codes.elementor_code}
                                onChange={() => null}
                            />
                        <Button variant="outlined" sx={{ minWidth: '140px' }} onClick={() => handleCopy(codes.elementor_code)}>Copy Webhook Link</Button>
                        </Box>
                        <Typography variant="body2" color="textSecondary">Turn on Advance Data toggle in elementor.</Typography>
                        </Box>
                    </Box>

                    <Box display="none" alignItems="center" gap={1}>
                        <Box sx={(theme) => ({
                            border: `1px solid ${theme.palette.divider}`,
                            padding: '5px 0',
                            color: 'rgba(51,56,63,1)',
                            borderRadius: '10px'
                        })}>
                            <Box display="flex" alignItems="center" justifyContent="center"><img src={ImageClass.customLogo} alt="Custom" height="70" width="70" /></Box>
                        </Box>
                        <CustomInput
                            fullWidth
                            onChange={() => null}
                            value={codes.script_code}
                        />
                        <Button variant="outlined" sx={{ minWidth: '140px' }} onClick={() => handleCopy(codes.script_code)}>Copy Script Link</Button>
                    </Box>
                </Box>} */}
    </CustomModal>;
};

export default ConnectExternalForm;